import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowLeft, ArrowRight, Compass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const TOUR = [
  { path: "/dashboard", title: "Your dashboard", body: "Your balances, a live countdown to your retirement day, the next service milestone and any missed agency match alerts, all in one place." },
  { path: "/accounts", title: "Accounts & Sync", body: "Link an IRA or brokerage through Plaid, drop in a TSP PDF or CSV statement, or type in your fund shares by hand." },
  { path: "/profile", title: "Profile & Pay", body: "Enter your age, salary and contribution rate. Add your birth date and service computation date so every retirement date is exact to the day." },
  { path: "/paychecks", title: "Paycheck Ledger", body: "Log each paycheck's contributions and agency match, spot missed match and export the ledger to PDF or CSV." },
  { path: "/pension", title: "FERS Pension", body: "Estimate your annuity, FERS Supplement and Social Security, and compare none, partial and full survivor elections side by side." },
  { path: "/best-date", title: "Best Retire Date", body: "Every month ahead is scanned for the biggest pension and supplement, with MRA, service and leave-year milestones." },
  { path: "/income", title: "Income Plan", body: "See your retirement paycheck year by year. Add your spouse for a household view and try the survivor scenario." },
  { path: "/checklist", title: "Retirement Checklist", body: "Steps unlock as your date nears. Get personalized HR questions, write down HR's answers, print a PDF and share a private link with your spouse." },
  { path: "/simulator", title: "What-If Simulator", body: "Change contributions, returns or retirement age and watch your projected balance and goal update instantly." },
  { path: "/funds", title: "Fund Prices", body: "Official daily TSP share prices with history charts for every fund." },
  { path: "/learn", title: "Content Hub", body: "Daily market recaps and guides on TSP, FERS and IRAs. Turn on the weekly recap email here. You can replay this tour any time from the sidebar." },
];
const KEY = "pw-tour-step";
const TourCtx = createContext({ start: () => {} });
export const useTour = () => useContext(TourCtx);

function SourceDialog({ open, sources, onDone }) {
  const [pick, setPick] = useState("");
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/onboarding/source", { source: pick, other }); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-lg [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="onboarding-source-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Welcome to PillarWealth</DialogTitle>
          <DialogDescription>Before a quick tour of the app: how did you hear about us?</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {sources.map((s) => (
            <button key={s} type="button" onClick={() => setPick(s)} data-testid={`onboarding-source-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`rounded-lg border px-3 py-2.5 text-sm text-left transition-colors duration-150 ${pick === s ? "border-cyan-600 bg-cyan-600/10 text-cyan-800 dark:text-cyan-300" : "border-border hover:bg-muted"}`}>{s}</button>
          ))}
        </div>
        {pick === "Other" && <Input autoFocus maxLength={200} placeholder="Tell us where" value={other} onChange={(e) => setOther(e.target.value)} data-testid="onboarding-source-other-input" />}
        <div><Button className="rounded-full w-full" disabled={!pick || busy || (pick === "Other" && !other.trim())} onClick={submit} data-testid="onboarding-source-submit">{busy ? "Saving..." : "Continue to the tour"}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

function TourCard({ i, go, end }) {
  const s = TOUR[i];
  const last = i === TOUR.length - 1;
  return (
    <div className="fixed z-50 inset-x-3 top-20 sm:inset-x-auto sm:top-auto sm:right-6 sm:bottom-6 sm:w-[380px] glass p-5 shadow-2xl border-cyan-600/40 fade-up" role="dialog" aria-label="App tour" data-testid="tour-card">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] uppercase tracking-widest text-cyan-700 dark:text-cyan-400 flex items-center gap-1.5"><Compass size={14} />Tour · step {i + 1} of {TOUR.length}</p>
        <button type="button" onClick={() => end(true)} aria-label="Skip tour" className="text-muted-foreground hover:text-foreground" data-testid="tour-close-button"><X size={16} /></button>
      </div>
      <p className="font-display text-lg font-semibold mt-2" data-testid="tour-step-title">{s.title}</p>
      <p className="text-sm text-muted-foreground mt-1 leading-relaxed" data-testid="tour-step-body">{s.body}</p>
      <div className="flex gap-1.5 mt-4" data-testid="tour-dots">
        {TOUR.map((t, k) => <button key={t.path} type="button" aria-label={`Go to step ${k + 1}`} onClick={() => go(k)} className={`h-1.5 rounded-full transition-all duration-200 ${k === i ? "w-6 bg-cyan-600" : "w-1.5 bg-slate-300 dark:bg-slate-600"}`} />)}
      </div>
      <div className="flex items-center justify-between mt-4">
        <Button variant="ghost" size="sm" onClick={() => end(true)} data-testid="tour-skip-button">Skip</Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full" disabled={i === 0} onClick={() => go(i - 1)} data-testid="tour-back-button"><ArrowLeft size={14} className="mr-1" />Back</Button>
          <Button size="sm" className="rounded-full" onClick={() => (last ? end(false) : go(i + 1))} data-testid="tour-next-button">{last ? "Finish" : <>Next<ArrowRight size={14} className="ml-1" /></>}</Button>
        </div>
      </div>
    </div>
  );
}

export function OnboardingProvider({ children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [step, setStep] = useState(() => { const v = sessionStorage.getItem(KEY); return v == null ? null : Number(v); });
  const [source, setSource] = useState(null);
  const go = useCallback((k) => { sessionStorage.setItem(KEY, String(k)); setStep(k); navigate(TOUR[k].path); }, [navigate]);
  const end = useCallback((skipped) => {
    sessionStorage.removeItem(KEY);
    setStep(null);
    api.post("/onboarding/complete", { skipped }).catch(() => {});
    if (!skipped) toast.success("You're all set. Replay the tour any time from the sidebar.");
  }, []);
  useEffect(() => {
    api.get("/onboarding/status").then(({ data }) => {
      if (!data.pending) return;
      if (!data.source_answered) setSource(data.sources);
      else if (sessionStorage.getItem(KEY) == null) go(0);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (step != null && TOUR[step] && pathname !== TOUR[step].path) navigate(TOUR[step].path); }, [step]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <TourCtx.Provider value={{ start: () => go(0) }}>
      {children}
      {source && <SourceDialog open sources={source} onDone={() => { setSource(null); go(0); }} />}
      {step != null && TOUR[step] && <TourCard i={step} go={go} end={end} />}
    </TourCtx.Provider>
  );
}

export function TakeTourButton({ className = "", testid = "take-tour-button", onClick }) {
  const { start } = useTour();
  return (
    <button type="button" onClick={() => { onClick?.(); start(); }} data-testid={testid}
      className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-150 text-slate-600 hover:bg-slate-900/5 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white ${className}`}>
      <Compass size={18} /> Take the tour
    </button>
  );
}
